import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, CircularProgress, Divider, Grid, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { IconCheck, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { Card, ContentTitle, InfoBanner, Input, Radio, DragAndDrop } from '~/components'
import { usePartner } from '~/contexts'
import { UserStatus } from '~/components/UserStatus'
import { FullPageLoader } from '~/components/FullPageLoader'
import { useCep, useUploadMultiFilesWithNoProposal } from '~/hooks'
import { userAccessStatusOptions } from '~/utils/options'
import { cpfSchema, phoneSchema } from '~/utils/yupSchema'
import { EAffiliatePartnerUserAccessStatus, EAffiliatePartnerUserFileType, AffiliatePartnerUserApprovedUpdateInput, AffiliatePartnerUserCreateInput, AffiliatePartnerUserGetQuery, AffiliatePartnerUserUpdateInput, EAffiliatePartnerUserStatus, useAffiliatePartnerUserGetQuery, useCreateAffiliatePartnerUserMutation, useUpdateAffiliatePartnerUserMutation, useUpdateAffiliatePartnerUserApprovedMutation } from '~/graphql/types'

type FormData = {
  name: string
  email: string
  phone: string
  cpf: string
  status: EAffiliatePartnerUserStatus
  accessStatus: EAffiliatePartnerUserAccessStatus
  captureSource: string
  address: {
    zipcode: string
    street: string
    number: string
    neighborhood: string
    complement?: string
    city: string
    state: string
    country: string
  }
  social: {
    linkedin?: string
    facebook?: string
    instagram?: string
    site?: string
  }
  professionalHistory: {
    companyName: string
    role: string
    segment: string
    observation?: string
  }[]
  files: {
    selfie: File[]
    addressProof: File[]
    idDocument: File[]
    cnpj: File[]
    socialContract: File[]
  }
}

type UserData = AffiliatePartnerUserGetQuery['affiliatePartnerUserGet']

// Partner para o qual todos os documentos do preposto são opcionais
const PARTNER_ALL_DOCS_OPTIONAL_ID = '67d423431a21207881316eb6'


export const UsersCreateEdit: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const isLowerSm = useMediaQuery(theme.breakpoints.down('sm'))
  const [selectedUser, setSelectedUser] = useState<null | UserData>(null)
  const [isUploading, setIsUploading] = useState(false)

  const isEdit = Boolean(id && selectedUser?._id)

  const { myPartner } = usePartner()
  const allDocsOptional = myPartner?._id === PARTNER_ALL_DOCS_OPTIONAL_ID

  const schema = useMemo(() => yup
    .object({
      name: yup.string().required('Nome é obrigatório'),
      email: yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
      phone: phoneSchema.required('Telefone é obrigatório'),
      cpf: cpfSchema.required('CPF é obrigatório'),
      status: yup.string(),
      accessStatus: yup.string(),
      captureSource: yup.string().required('Fonte de captação é obrigatória'),
      address: yup.object({
        zipcode: yup.string().required('CEP é obrigatório'),
        street: yup.string().required('Rua é obrigatória'),
        number: yup.string().required('Número é obrigatório'),
        neighborhood: yup.string().required('Bairro é obrigatório'),
        complement: yup.string().optional(),
        city: yup.string().required('Cidade é obrigatória'),
        state: yup.string().required('Estado é obrigatório'),
        country: yup.string().required('País é obrigatório'),
      }),
      social: yup.object({
        linkedin: yup.string().url('LinkedIn deve ser uma URL válida').nullable(),
        facebook: yup.string().url('Facebook deve ser uma URL válida').nullable(),
        instagram: yup.string().url('Instagram deve ser uma URL válida').nullable(),
        site: yup.string().url('Site deve ser uma URL válida').nullable(),
      }),
      professionalHistory: yup.array().of(
        yup.object({
          companyName: yup.string().required('Empresa é obrigatória'),
          role: yup.string().required('Função é obrigatória'),
          segment: yup.string().required('Segmento é obrigatório'),
          observation: yup.string().optional(),
        })
      ),
      files: yup.object({
        selfie: yup.array().test('required', 'Selfie é obrigatória', (value) => {
          if (allDocsOptional) return true
          if (isEdit && selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.selfie)) return true
          return !!(value && value.length > 0)
        }),
        addressProof: yup.array().test('required', 'Comprovante de endereço é obrigatório', (value) => {
          if (allDocsOptional) return true
          if (isEdit && selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.addressProof)) return true
          return !!(value && value.length > 0)
        }),
        idDocument: yup.array().test('required', 'Documento de identidade é obrigatório', (value) => {
          if (allDocsOptional) return true
          if (isEdit && selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.idDocument)) return true
          return !!(value && value.length > 0)
        }),
        cnpj: yup.array().optional(),
        socialContract: yup.array().optional(),
      })
    })
    .required(), [isEdit, selectedUser, allDocsOptional])

  const formMethods = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      address: { country: 'Brasil' },
      professionalHistory: [],
      files: {
        selfie: [],
        addressProof: [],
        idDocument: [],
        cnpj: [],
        socialContract: [],
      }
    }
  })

  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: 'professionalHistory'
  })

  const { fetchCep, isLoading: isFetchingCep } = useCep({ showErrorMessage: true })
  const [uploadFiles] = useUploadMultiFilesWithNoProposal()

  const zipcode = useWatch({ control: formMethods.control, name: 'address.zipcode' })
  const cleanZipcode = zipcode?.replace(/\D/g, '') || ''
  const canSearchCep = cleanZipcode.length === 8

  const handleSearchCep = useCallback(async () => {
    if (!canSearchCep) return
    const response = await fetchCep(cleanZipcode)
    if (response.isSuccess && response.data) {
      formMethods.setValue('address.street', response.data.logradouro, { shouldValidate: true })
      formMethods.setValue('address.neighborhood', response.data.bairro, { shouldValidate: true })
      formMethods.setValue('address.city', response.data.localidade, { shouldValidate: true })
      formMethods.setValue('address.state', response.data.uf, { shouldValidate: true })
    }
  }, [canSearchCep, cleanZipcode, fetchCep, formMethods])

  const { loading: getAffiliatePartnerUserIsLoading } = useAffiliatePartnerUserGetQuery({
    variables: {
      id: id || ''
    },
    skip: Boolean(!id),
    onCompleted(data) {
      setSelectedUser(data.affiliatePartnerUserGet)
    },
    onError(error) {
      toast.error(error.message)
    },
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    setSelectedUser(null)
  }, [id])

  const [createAffiliatePartnerUser, { loading: createIsLoading }] = useCreateAffiliatePartnerUserMutation({
    onCompleted: () => {
      setIsUploading(false)
      toast.success('Um preposto foi cadastrado!')
      navigate('/app/users')
    },
    onError: (error) => {
      setIsUploading(false)
      toast.error(error.message)
    }
  })

  const [updateAffiliatePartnerUser, { loading: updateIsLoading }] = useUpdateAffiliatePartnerUserMutation({
    onCompleted: (data) => {
      setIsUploading(false)
      setSelectedUser(null)
      toast.success(`O preposto ${data.updateAffiliatePartnerUser.name} foi alterado!`)
      navigate('/app/users')
    },
    onError: (error) => {
      setIsUploading(false)
      toast.error(error.message)
    }
  })

  const [updateAffiliatePartnerUserApproved, { loading: updateApprovedIsLoading }] = useUpdateAffiliatePartnerUserApprovedMutation({
    onCompleted: (data) => {
      setIsUploading(false)
      setSelectedUser(null)
      toast.success('O preposto foi alterado!')
      navigate('/app/users')
    },
    onError: (error) => {
      setIsUploading(false)
      toast.error(error.message)
    }
  })

  const isSubmitting = isUploading || createIsLoading || updateIsLoading || updateApprovedIsLoading


  async function onSubmit(data: FormData) {
    setIsUploading(true)
    const uploadedFilesKeys: { type: EAffiliatePartnerUserFileType, fileKey: string }[] = []

    const filesToUpload = [
      { type: EAffiliatePartnerUserFileType.selfie, files: data.files?.selfie },
      { type: EAffiliatePartnerUserFileType.addressProof, files: data.files?.addressProof },
      { type: EAffiliatePartnerUserFileType.idDocument, files: data.files?.idDocument },
      { type: EAffiliatePartnerUserFileType.cnpj, files: data.files?.cnpj },
      { type: EAffiliatePartnerUserFileType.socialContract, files: data.files?.socialContract },
    ].filter(item => item.files && item.files.length > 0)

    if (filesToUpload.length > 0) {
      const result = await uploadFiles({
        files: filesToUpload.map(item => item.files as File[]),
        types: filesToUpload.map(item => item.type as any) // Casting as any due to mismatch with EProposalFileType
      })

      if (!result) {
        setIsUploading(false)
        return
      }

      result.forEach((uploadedFile, index) => {
        uploadedFilesKeys.push({
          type: filesToUpload[index].type,
          fileKey: uploadedFile.file.key
        })
      })
    }

    // Mantém arquivos existentes se não foram re-enviados
    if (isEdit && selectedUser?.files) {
      selectedUser.files.forEach(existingFile => {
        if (!uploadedFilesKeys.find(f => f.type === existingFile.type)) {
          uploadedFilesKeys.push({ type: existingFile.type, fileKey: existingFile.file.key })
        }
      })
    }

    const createUser: AffiliatePartnerUserCreateInput = {
      email: data.email,
      name: data.name,
      phone: data.phone,
      cpf: data.cpf.replace(/\D/g, ''),
      captureSource: data.captureSource,
      address: {
        ...data.address,
        zipcode: data.address.zipcode.replace(/\D/g, ''),
        country: data.address.country || 'Brasil',
        complement: data.address.complement || undefined
      },
      linkedin: data.social.linkedin || undefined,
      facebook: data.social.facebook || undefined,
      instagram: data.social.instagram || undefined,
      site: data.social.site || undefined,
      files: uploadedFilesKeys,
      professionalHistory: (data.professionalHistory || []).map(ph => ({
        companyName: ph.companyName,
        role: ph.role,
        segment: ph.segment,
        observation: ph.observation || ''
      }))
    }

    if (id && selectedUser) {
      if (isApproved) {
        const updateApproved: AffiliatePartnerUserApprovedUpdateInput = {
          email: data.email,
          accessStatus: data.accessStatus
        }
        updateAffiliatePartnerUserApproved({ variables: { userUpdateInput: updateApproved, id } })
      } else {
        const updateUser: AffiliatePartnerUserUpdateInput = {
          ...createUser
          // accessStatus não é suportado no AffiliatePartnerUserUpdateInput pelo GraphQL atual
        }
        updateAffiliatePartnerUser({ variables: { params: updateUser, id } })
      }
    } else {
      createAffiliatePartnerUser({ variables: { params: createUser } })
    }
  }

  useEffect(() => {
    if (selectedUser) {
      formMethods.reset({
        name: selectedUser.name,
        email: selectedUser.email,
        phone: selectedUser.phone || '',
        cpf: selectedUser.cpf || '',
        status: selectedUser.status,
        accessStatus: selectedUser.accessStatus,
        captureSource: selectedUser.captureSource || '',
        address: {
          zipcode: selectedUser.address?.zipcode || '',
          street: selectedUser.address?.street || '',
          number: selectedUser.address?.number || '',
          neighborhood: selectedUser.address?.neighborhood || '',
          complement: selectedUser.address?.complement || '',
          city: selectedUser.address?.city || '',
          state: selectedUser.address?.state || '',
          country: selectedUser.address?.country || 'Brasil',
        },
        social: {
          linkedin: selectedUser.linkedin || '',
          facebook: selectedUser.facebook || '',
          instagram: selectedUser.instagram || '',
          site: selectedUser.site || '',
        },
        professionalHistory: selectedUser.professionalHistory?.map(ph => ({
          companyName: ph.companyName || '',
          role: ph.role || '',
          segment: ph.segment || '',
          observation: ph.observation || ''
        })) || [],
        files: {
          selfie: [],
          addressProof: [],
          idDocument: [],
          cnpj: [],
          socialContract: [],
        }
      })
    } else {
      formMethods.reset()
    }
  }, [selectedUser])

  const isRejected = selectedUser?.status === EAffiliatePartnerUserStatus.rejected
  const isApproved = selectedUser?.status === EAffiliatePartnerUserStatus.approved

  const isDisabled = isRejected
  const isRejectedUser = selectedUser?.status === EAffiliatePartnerUserStatus.rejected

  if (getAffiliatePartnerUserIsLoading) return <FullPageLoader />

  return (
    <Box>
      <Card>
        <ContentTitle
          title={isEdit ? `Alterar preposto ${selectedUser?.name}` : 'Cadastrar prepostos'}
          style={{ padding: '0px 0px 1.6rem 0px' }}
          description={`Complete os dados para ${isEdit ? 'alterar' : 'cadastrar'} um preposto`}
          breadcrumbLinks={{ currentLink: isEdit ? 'Alterar preposto' : 'Cadastrar preposto', links: [{ href: '/app/users', label: 'prepostos' }] }}
        />
        {isRejectedUser &&
          <InfoBanner type='warning' text='Preposto reprovado não pode ser editado!' />
        }

        <Divider sx={{ marginBottom: '2rem' }} />

        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit((data) => onSubmit(data))} style={{ display: 'flex', flexFlow: 'column', gap: '1rem' }}>
            <Grid container spacing={2}>
              {isEdit && selectedUser?.status && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant='h6'>Status:</Typography>
                    <UserStatus status={selectedUser.status} />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant='h6' sx={{ mb: 1 }}>Dados Pessoais</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Input label='Nome' name='name' disabled={isDisabled || isApproved} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input label='E-Mail' name='email' disabled={isDisabled} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input mask='phone' label='Telefone' name='phone' disabled={isDisabled || isApproved} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input mask='cpf' label='CPF' name='cpf' disabled={isDisabled || isApproved} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input label='Fonte de Captação' name='captureSource' disabled={isDisabled || isApproved} />
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant='h6' sx={{ mb: 1 }}>Endereço</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Input
                  mask='cep'
                  label='CEP'
                  name='address.zipcode'
                  disabled={isDisabled || isApproved}
                  icons={{
                    end: {
                      padding: '0 8px',
                      element: (
                        <IconButton
                          aria-label='Buscar CEP'
                          onClick={handleSearchCep}
                          disabled={!canSearchCep || isFetchingCep || isDisabled || isApproved}
                          edge='end'
                        >
                          {isFetchingCep ? <CircularProgress size={20} color='inherit' /> : <IconSearch />}
                        </IconButton>
                      )
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Input label='Logradouro' name='address.street' disabled={isDisabled || isApproved} />
              </Grid>
              <Grid item xs={12} md={2}>
                <Input label='Número' name='address.number' disabled={isDisabled || isApproved} />
              </Grid>
              <Grid item xs={12} md={5}>
                <Input label='Bairro' name='address.neighborhood' disabled={isDisabled || isApproved} />
              </Grid>
              <Grid item xs={12} md={5}>
                <Input label='Complemento' name='address.complement' disabled={isDisabled || isApproved} />
              </Grid>
              <Grid item xs={12} md={5}>
                <Input label='Cidade' name='address.city' disabled={isDisabled || isApproved} />
              </Grid>
              <Grid item xs={12} md={3}>
                <Input label='Estado' name='address.state' disabled={isDisabled || isApproved} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Input label='País' name='address.country' disabled={isDisabled || isApproved} />
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant='h6' sx={{ mb: 1 }}>Redes Sociais</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Input label='LinkedIn' name='social.linkedin' disabled={isDisabled || isApproved} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input label='Facebook' name='social.facebook' disabled={isDisabled || isApproved} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input label='Instagram' name='social.instagram' disabled={isDisabled || isApproved} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input label='Site' name='social.site' disabled={isDisabled || isApproved} />
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant='h6'>Histórico Profissional</Typography>
                  <Button
                    size='small'
                    startIcon={<IconPlus />}
                    onClick={() => append({ companyName: '', role: '', segment: '', observation: '' })}
                    disabled={isDisabled || isApproved}
                  >
                    Adicionar Empresa
                  </Button>
                </Box>
              </Grid>
              {fields.map((field, index) => (
                <Grid item xs={12} key={field.id}>
                  <Card sx={{ p: 2, background: theme.palette.grey[50] }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Input label='Empresa' name={`professionalHistory.${index}.companyName`} disabled={isDisabled || isApproved} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Input label='Função' name={`professionalHistory.${index}.role`} disabled={isDisabled || isApproved} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Input label='Segmento' name={`professionalHistory.${index}.segment`} disabled={isDisabled || isApproved} />
                      </Grid>
                      <Grid item xs={12} md={10}>
                        <Input label='Observação' name={`professionalHistory.${index}.observation`} disabled={isDisabled || isApproved} />
                      </Grid>
                      <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <IconButton color='error' onClick={() => remove(index)} disabled={isDisabled || isApproved}>
                          <IconTrash />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant='h6' sx={{ mb: 1 }}>Documentos</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <DragAndDrop
                  $variant='secondary'
                  name='files.selfie'
                  label={allDocsOptional ? 'Selfie (opcional)' : 'Selfie'}
                  fileDescription='Tire uma foto do seu rosto'
                  accepted={{ images: true, pdf: true }}
                  disabled={isDisabled || isApproved}
                  {...(selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.selfie) ? {
                    fileName: selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.selfie)?.file.fileName,
                    fileUrl: `${import.meta.env.VITE_APP_REST_API_URL}files/download/file/${selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.selfie)?.file.key}`,
                    $completed: true
                  } : {})}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DragAndDrop
                  $variant='secondary'
                  name='files.addressProof'
                  label={allDocsOptional ? 'Comprovante de Endereço (opcional)' : 'Comprovante de Endereço'}
                  fileDescription='Upload do comprovante de endereço'
                  accepted={{ images: true, pdf: true }}
                  disabled={isDisabled || isApproved}
                  {...(selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.addressProof) ? {
                    fileName: selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.addressProof)?.file.fileName,
                    fileUrl: `${import.meta.env.VITE_APP_REST_API_URL}files/download/file/${selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.addressProof)?.file.key}`,
                    $completed: true
                  } : {})}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DragAndDrop
                  $variant='secondary'
                  name='files.idDocument'
                  label={allDocsOptional ? 'Documento de Identidade (opcional)' : 'Documento de Identidade'}
                  fileDescription='Upload do RG ou CNH'
                  accepted={{ images: true, pdf: true }}
                  disabled={isDisabled || isApproved}
                  {...(selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.idDocument) ? {
                    fileName: selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.idDocument)?.file.fileName,
                    fileUrl: `${import.meta.env.VITE_APP_REST_API_URL}files/download/file/${selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.idDocument)?.file.key}`,
                    $completed: true
                  } : {})}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DragAndDrop
                  $variant='secondary'
                  name='files.cnpj'
                  label='Cartão CNPJ (opcional)'
                  fileDescription='Upload do cartão CNPJ'
                  accepted={{ images: true, pdf: true }}
                  disabled={isDisabled || isApproved}
                  {...(selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.cnpj) ? {
                    fileName: selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.cnpj)?.file.fileName,
                    fileUrl: `${import.meta.env.VITE_APP_REST_API_URL}files/download/file/${selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.cnpj)?.file.key}`,
                    $completed: true
                  } : {})}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DragAndDrop
                  $variant='secondary'
                  name='files.socialContract'
                  label='Contrato Social (opcional)'
                  fileDescription='Upload do contrato social'
                  accepted={{ images: true, pdf: true }}
                  disabled={isDisabled || isApproved}
                  {...(selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.socialContract) ? {
                    fileName: selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.socialContract)?.file.fileName,
                    fileUrl: `${import.meta.env.VITE_APP_REST_API_URL}files/download/file/${selectedUser?.files?.find(f => f.type === EAffiliatePartnerUserFileType.socialContract)?.file.key}`,
                    $completed: true
                  } : {})}
                />
              </Grid>

              {isEdit && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant='h6' sx={{ mb: 1 }}>Acesso</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant='subtitle2' color='textSecondary'>Status de Acesso</Typography>
                    <Radio row name='accessStatus' options={userAccessStatusOptions} disabled={!isApproved} />
                  </Box>
                </Grid>
              )}
            </Grid>

            <Box sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexFlow: isLowerSm ? 'column-reverse' : 'row',
              gap: isLowerSm ? '1rem' : '0'
            }}>
              <Link style={{ display: 'block', maxWidth: isLowerSm ? '100%' : '160px', width: '100%' }} to='/app/users'>
                <Button
                  disabled={isSubmitting}
                  fullWidth
                  type='button'
                  color='secondary'
                >
                  Cancelar
                </Button>
              </Link>
              <Button
                disabled={isSubmitting || isDisabled}
                sx={{ maxWidth: isLowerSm ? '100%' : '220px' }}
                fullWidth
                type='submit'
                startIcon={isSubmitting ? <CircularProgress size={20} color='inherit' /> : <IconCheck />}
              >
                {isSubmitting ? 'Enviando...' : (isEdit ? 'Alterar usuário' : 'Cadastrar usuário')}
              </Button>
            </Box>
          </form>
        </FormProvider>
      </Card>
    </Box>
  )
}
